import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { useApolloClient } from '@apollo/client';
import {
    ProductionPlanForDateDocument,
    ProductionPlanForDateQuery,
    ProductionPlanForDateQueryVariables,
    RoleId,
    useCurrentUserQuery,
    useDeleteProductionPlanMutation,
    useProductionPlanForDateQuery,
    useProductionPlanActualsQuery,
    useProductionPlanningPageQuery,
    useUpsertProductionPlanMutation,
} from '../../../../services';
import {
    DDatePicker,
    DModal,
    DPage,
    DPageBody,
    DPageHeader,
    DPageLoader,
} from '../../../dum';
import {
    copyToClipboard,
    DATE_FORMAT,
    formatFloat,
    getStringFromDate,
} from '../../../../helpers';
import { useAppDispatch } from '../../../../app/hooks';
import { pushMessage } from '../../../../features/global-messages/global-messages-slice';
import { ProductionPlanRow } from './components/production-plan-row';
import {
    RatePair,
    RateState,
    resolveRateState,
    useRatesCache,
} from './components/prediction';
import { isInventoryTracked as isInventoryTrackedForType } from '../../../../services/entities';
import { useMachineProductsCache } from './components/machine-products';
import { PedidosDrawer, ProductPlanState } from './components/pedidos-drawer';
import { MachinePickerDialog } from './components/machine-picker-dialog';
import {
    computeCoverage,
    productionNeededKilosByProduct,
} from './components/coverage';
import {
    addProductHours,
    candidateRows,
    CLICK_REFUSAL_MESSAGE,
    freeHoursOf,
    planClickHours,
} from './components/plan-allocation';
import { WhatsAppMessage } from './components/whatsapp-message';
import { joinNotes, splitNotes } from './components/notes-helpers';
import {
    DEFAULT_EFFICIENCY,
    DEFAULT_SHIFT_HOURS,
    effectiveKgHr,
    effectiveShiftHours,
    HOURS_EPSILON,
    normalizeHours,
    nextRowProductKey,
} from './components/plan-helpers';

// One product planned on a row's machine, consuming `hours` of the turno.
// Deliberately carries no pedido link: 79% of products are wanted by 2+ live
// pedidos at once, so this output serves the priority queue, not one pedido.
// The drawer derives coverage by product instead.
export interface PlanRowProductState {
    // stable local key for React lists (products have no id until saved)
    key: string;
    id: number | null;
    product_id: number | null;
    hours: number;
    // Percentage (80 = 80%) of the machine's historical kg/hr this line is
    // expected to hit. Scales every predicted kilo derived from it.
    efficiency: number;
}

export interface PlanRowState {
    // stable local key for React lists (rows have no id until saved)
    key: string;
    id: number | null;
    machine_id: number | null;
    // How long THIS machine's turno is. Null — the normal case — means it runs
    // the plan's Horas. It exists because the sum rule is a hard save-blocker:
    // a machine down two hours for maintenance would otherwise force the planner
    // to fabricate hours or leave the whole plan unsaveable.
    shift_hours: number | null;
    // A row may split its machine across several products.
    products: PlanRowProductState[];
    // Row notes as a bullet list (stored newline-separated in the DB).
    notes: string[];
    employee_ids: number[];
}

// One product's predicted contribution from each machine row. The
// parent owns this because the closed-row tooltip needs the whole plan, not
// only the row currently being rendered.
export interface PlannedProductContribution {
    machineName: string;
    kilos: number;
}

export interface MachineOption {
    id: number;
    name: string;
    branch_id?: number | null;
    order_production_type_id?: number | null;
}

export interface ProductOption {
    id: number;
    compound_description: string;
    order_production_type_id?: number | null;
    // Converts predicted kilos into bultos. 0 means the product has no group
    // weight, in which case bultos are shown as "—" rather than fabricated.
    current_group_weight?: number | null;
}

export interface EmployeeOption {
    id: number;
    fullname: string;
    order_production_type_id?: number | null;
    branch_id?: number | null;
    is_inactive?: boolean | null;
    // 1 = operador, 0 = ayudante. Sections the row's employee picker.
    is_leader?: number | null;
}

export interface InventoryEntry {
    // Physically on hand — INCLUDING stock already sold on a sale that has not
    // reached Entregado, because the goods only leave the warehouse then.
    kilos: number;
    groups: number;
    // The part of the above already spoken for by such a sale.
    kilos_committed: number;
    groups_committed: number;
}

const SHIFTS = [1, 2, 3];

// order_request_statuses: 1 = Pendiente, 2 = En producción. The drawer defaults
// to committed pedidos only.
const IN_PRODUCTION = 2;

// 1er / 2do / 3er — the WhatsApp message uses the Spanish ordinal.
const TURNO_ORDINAL: Record<number, string> = {
    1: '1er',
    2: '2do',
    3: '3er',
};

// Discarding unsaved rows (header change or import over dirty edits) always
// asks first — the plan editor holds real, unsaved work.
const DISCARD_CONFIRM = 'Hay cambios sin guardar. ¿Descartarlos?';

// Tab indices. The editor is index 0 because that is where planning starts; the
// WhatsApp tab is where it ends.
const PLAN_TAB = 0;
const WHATSAPP_TAB = 1;

let localRowCounter = 0;
const nextRowKey = () => `row-${localRowCounter++}`;

const PLANNING_SELECTORS_STORAGE_KEY = 'ProductionPlanning_Selectors';

interface StoredPlanningSelectors {
    date?: string;
    shift?: number;
    branchId?: number;
    productionTypeId?: number;
}

const readStoredPlanningSelectors = (): StoredPlanningSelectors => {
    try {
        const raw = window.localStorage.getItem(
            PLANNING_SELECTORS_STORAGE_KEY,
        );
        if (!raw) return {};

        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};

        const value = parsed as Record<string, unknown>;
        const date =
            typeof value.date === 'string' &&
            /^\d{4}-\d{2}-\d{2}$/.test(value.date) &&
            dayjs(value.date).isValid()
                ? value.date
                : undefined;
        const shift =
            typeof value.shift === 'number' && SHIFTS.includes(value.shift)
                ? value.shift
                : undefined;
        // Null was previously used for "Todas" / "Todos". Those options are
        // no longer supported, so an old null is treated as missing and the
        // normal concrete default is applied below.
        const branchId =
            typeof value.branchId === 'number' &&
            Number.isInteger(value.branchId) &&
            value.branchId > 0
                ? value.branchId
                : undefined;
        const productionTypeId =
            typeof value.productionTypeId === 'number' &&
            Number.isInteger(value.productionTypeId) &&
            value.productionTypeId > 0
                ? value.productionTypeId
                : undefined;

        return { date, shift, branchId, productionTypeId };
    } catch {
        return {};
    }
};

// Source rows returned by the import fetch (ProductionPlanForDate).
type SourcePlanRow = NonNullable<
    ProductionPlanForDateQuery['getProductionPlan']
>['rows'][number];

export function ProductionPlanning() {
    const dispatch = useAppDispatch();
    const client = useApolloClient();
    const storedSelectorsRef = useRef(readStoredPlanningSelectors());
    const storedSelectors = storedSelectorsRef.current;
    const { ensure: ensureRatesRaw, get: getRateRaw } = useRatesCache();
    // Adjust the kg/hr predictions to single-product runs only — the same
    // packed-rate filter the Rendimiento surfaces use, restricting the baseline
    // to productions that made one product (clean per-product hours). Bound into
    // wrapped ensure/get below so every call site (including the child rows that
    // receive `getRate`) inherits it, and the fetch effect re-runs on toggle.
    const [singleProductOnly, setSingleProductOnly] = useState(false);
    const ensureRates = useCallback(
        (pairs: RatePair[], fromDate?: string) =>
            ensureRatesRaw(pairs, fromDate, singleProductOnly),
        [ensureRatesRaw, singleProductOnly],
    );
    const getRate = useCallback(
        (machineId: number, productId: number | null, fromDate?: string) =>
            getRateRaw(machineId, productId, fromDate, singleProductOnly),
        [getRateRaw, singleProductOnly],
    );
    const {
        ensure: ensureMachineProduct,
        get: getMachineProduct,
    } = useMachineProductsCache();

    const [date, setDate] = useState<string>(
        storedSelectors.date ?? dayjs().format(DATE_FORMAT),
    );
    const [shift, setShift] = useState<number>(storedSelectors.shift ?? 1);
    // How many hours the planned turno lasts. This used to be a display-only
    // knob; it became PLAN DATA when rows started splitting the shift between
    // products, because every row's products must add up to it. So it now
    // persists, dirties the plan, and — since changing it invalidates every
    // row's total — routes through guardedHeaderChange like the other headers.
    const [shiftHours, setShiftHours] =
        useState<number>(DEFAULT_SHIFT_HOURS);
    const [branchId, setBranchId] = useState<number | null>(
        storedSelectors.branchId ?? null,
    );
    const [productionTypeId, setProductionTypeId] = useState<number | null>(
        storedSelectors.productionTypeId ?? null,
    );

    // Editor vs the outgoing WhatsApp messages. Plan state is shared, so
    // switching tabs never discards anything and does not need the dirty guard.
    const [tab, setTab] = useState<number>(PLAN_TAB);

    const [planId, setPlanId] = useState<number | null>(null);
    // Plan-level notes are two typed bullet lists: parejas (persisted under the
    // legacy `notes` column) and productos (the new `product_notes` column).
    const [pairNotes, setPairNotes] = useState<string[]>([]);
    const [productNotes, setProductNotes] = useState<string[]>([]);
    const [rows, setRows] = useState<PlanRowState[]>([]);
    const [pedidosOpen, setPedidosOpen] = useState(false);
    // The drawer opens on committed pedidos (En producción) only; the toggle
    // brings the Pendientes back. Default is the focused list because that is
    // what a turno is normally planned against.
    const [pedidosIncludePending, setPedidosIncludePending] = useState(false);
    // Product whose machine is being chosen after a drawer click. null = closed.
    const [machinePickerProductId, setMachinePickerProductId] = useState<
        number | null
    >(null);
    // Which row is open for editing. Only one at a time, and none on load: a
    // 20-machine plan otherwise mounts ~60 MUI Autocompletes, each holding the
    // full product catalogue, which is what made the page slow to type in.
    // Collapsed rows render a text summary instead.
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);

    // Tracks unsaved edits so header changes / imports confirm before wiping
    // rows. Set on any row/notes edit; cleared whenever the reset effect
    // rebuilds a clean baseline (fresh seed, loaded plan, or after a save).
    const [dirty, setDirty] = useState(false);

    const { data: currentUserData } = useCurrentUserQuery();
    // Writes are gated to Produccion (main). Super/General keep access; everyone
    // else — assistants included — sees the plan read-only, same rule as the
    // Pedidos por surtir board's canReorder.
    const canEdit =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Production].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    const { data: optionsData, loading: optionsLoading } =
        useProductionPlanningPageQuery({
            variables: { excludeDiscontinued: true },
        });

    const {
        data: planData,
        loading: planLoading,
        refetch: refetchPlan,
    } = useProductionPlanForDateQuery({
        variables: {
            date: getStringFromDate(date),
            shift,
            branchId,
        },
        skip: !date,
        fetchPolicy: 'network-only',
    });

    // What was really produced for this exact slot. Inventory already contains
    // it, so the forecast must be netted down by it — otherwise reopening a past
    // plan adds a shift of output that has already happened. Also gives each row
    // a planned-vs-real comparison, which the plan never had before.
    const {
        data: actualsData,
        loading: actualsLoading,
    } = useProductionPlanActualsQuery({
        variables: {
            date: getStringFromDate(date),
            shift,
            branchId,
        },
        skip: !date,
        fetchPolicy: 'network-only',
    });

    const [upsertPlan, { loading: saving }] =
        useUpsertProductionPlanMutation();
    const [deletePlan, { loading: deleting }] =
        useDeleteProductionPlanMutation();

    const branches = useMemo(
        () => optionsData?.getBranches || [],
        [optionsData],
    );
    const productionTypes = useMemo(
        () => optionsData?.getOrderProductionTypes || [],
        [optionsData],
    );
    const machines = useMemo<MachineOption[]>(
        () => optionsData?.getMachines || [],
        [optionsData],
    );
    const products = useMemo<ProductOption[]>(
        () => optionsData?.getProducts || [],
        [optionsData],
    );
    const employees = useMemo<EmployeeOption[]>(
        () => optionsData?.getEmployees || [],
        [optionsData],
    );

    // The existing slot-wide actuals query already includes production lines
    // for every machine in the selected date/turno/sucursal. A machine belongs
    // to one production type, so matching its type here tells the planner that
    // this exact selector combination already has captured production without
    // adding another network request just for the warning.
    const hasRelatedOrderProductions = useMemo(() => {
        if (
            !branchId ||
            !productionTypeId ||
            actualsLoading ||
            !actualsData
        ) {
            return false;
        }
        const machineTypeById = new Map(
            machines.map((machine) => [
                machine.id,
                machine.order_production_type_id,
            ]),
        );
        return (actualsData.getProductionPlanActuals || []).some(
            (actual) =>
                typeof actual.machine_id === 'number' &&
                machineTypeById.get(actual.machine_id) === productionTypeId,
        );
    }, [actualsData, actualsLoading, branchId, machines, productionTypeId]);

    // Machines belong to a branch and a production type. Narrow to the picked
    // branch (mirrors order-productions) and the picked production type; a null
    // filter means "all". getMachines already excludes discontinued machines.
    const filteredMachines = useMemo(
        () =>
            machines.filter((machine) => {
                const branchOk = branchId
                    ? machine.branch_id === branchId
                    : true;
                const typeOk = productionTypeId
                    ? machine.order_production_type_id === productionTypeId
                    : true;
                return branchOk && typeOk;
            }),
        [machines, branchId, productionTypeId],
    );

    // Default the filters once options load: Caucel branch + Corte y empaque
    // production type (the most common shift-plan combo). Applied a single time
    // via the ref so the user is free to change or clear them afterward —
    // matched by name to avoid hardcoding row ids.
    const defaultsAppliedRef = useRef(false);
    useEffect(() => {
        if (defaultsAppliedRef.current) return;
        if (branches.length === 0 || productionTypes.length === 0) return;
        const caucel = branches.find(
            (branch) => branch.name.trim().toLowerCase() === 'caucel',
        );
        const corteYEmpaque = productionTypes.find(
            (type) => type.name.trim().toLowerCase() === 'corte y empaque',
        );
        if (storedSelectors.branchId === undefined && caucel) {
            setBranchId(caucel.id);
        }
        if (
            storedSelectors.productionTypeId === undefined &&
            corteYEmpaque
        ) {
            setProductionTypeId(corteYEmpaque.id);
        }
        defaultsAppliedRef.current = true;
    }, [branches, productionTypes, storedSelectors]);

    // Keep the planner's concrete general selectors across a browser refresh.
    useEffect(() => {
        try {
            window.localStorage.setItem(
                PLANNING_SELECTORS_STORAGE_KEY,
                JSON.stringify({ date, shift, branchId, productionTypeId }),
            );
        } catch {
            // Storage can be unavailable in restricted browser contexts; the
            // page remains usable with the in-memory selector state.
        }
    }, [date, shift, branchId, productionTypeId]);

    // Reset the editable plan whenever the fetched plan or the machine filters
    // change. If a plan already exists for the combo, load its saved rows. If
    // none exists, seed one row per filtered (non-discontinued) machine so the
    // user starts from the full machine list instead of an empty plan; upsert
    // creates the plan on save. Changing branch/type rebuilds this seed. Every
    // run establishes a clean baseline, so the dirty flag resets here.
    useEffect(() => {
        const plan = planData?.getProductionPlan;
        setPlanId(plan?.id ?? null);
        setPairNotes(splitNotes(plan?.notes ?? ''));
        setProductNotes(splitNotes(plan?.product_notes ?? ''));
        setShiftHours(
            normalizeHours(plan?.shift_hours || DEFAULT_SHIFT_HOURS),
        );
        if (plan) {
            const selectedMachineIds = new Set(
                filteredMachines.map((machine) => machine.id),
            );
            const planRows = plan.rows
                .filter(
                    (planRow) =>
                        planRow.machine_id == null ||
                        selectedMachineIds.has(planRow.machine_id),
                )
                .map((planRow) => ({
                    key: `db-${planRow.id}`,
                    id: planRow.id,
                    machine_id: planRow.machine_id ?? null,
                    shift_hours:
                        planRow.shift_hours == null
                            ? null
                            : normalizeHours(planRow.shift_hours),
                    products: planRow.products.map((rowProduct) => ({
                        key: `db-product-${rowProduct.id}`,
                        id: rowProduct.id,
                        product_id: rowProduct.product_id ?? null,
                        hours: normalizeHours(rowProduct.hours ?? 0),
                        // The column is NOT NULL with a default, so this only
                        // covers a plan fetched before the field existed.
                        efficiency:
                            rowProduct.efficiency ?? DEFAULT_EFFICIENCY,
                    })),
                    notes: splitNotes(planRow.notes),
                    employee_ids: planRow.employees.map(
                        (employee) => employee.id,
                    ),
                }));
            const savedMachineIds = new Set(
                planRows
                    .map((planRow) => planRow.machine_id)
                    .filter((machineId): machineId is number => machineId !== null),
            );
            const missingMachineRows = filteredMachines
                .filter((machine) => !savedMachineIds.has(machine.id))
                .map((machine) => ({
                    key: nextRowKey(),
                    id: null,
                    machine_id: machine.id,
                    shift_hours: null,
                    products: [],
                    notes: [],
                    employee_ids: [],
                }));
            setRows([...planRows, ...missingMachineRows]);
        } else {
            // Fresh seed (no saved plan yet): one row per filtered machine,
            // ordered alphabetically by machine name so the editor starts in a
            // predictable order. Once the plan is saved its rows come back in
            // stored `position` order (see the loaded branch above), so the
            // user's arranged order — not this alphabetical seed — is preserved.
            // numeric:true gives a natural sort (M2 before M10); sort a copy so
            // the memoized filteredMachines array isn't mutated.
            setRows(
                [...filteredMachines]
                    .sort((a, b) =>
                        a.name.localeCompare(b.name, undefined, {
                            numeric: true,
                            sensitivity: 'base',
                        }),
                    )
                    .map((machine) => ({
                        key: nextRowKey(),
                        id: null,
                        machine_id: machine.id,
                        // Seeded rows inherit the plan's Horas; an override is
                        // always something the planner sets deliberately.
                        shift_hours: null,
                        products: [],
                        notes: [],
                        employee_ids: [],
                    })),
            );
        }
        setDirty(false);
    }, [planData, filteredMachines]);

    const inventory = useMemo(() => {
        const map = new Map<number, InventoryEntry>();
        (optionsData?.getProductsInventory || []).forEach((entry) => {
            if (!entry.product_id) return;
            map.set(entry.product_id, {
                kilos: entry.kilos || 0,
                groups: entry.groups || 0,
                kilos_committed: entry.kilos_committed || 0,
                groups_committed: entry.groups_committed || 0,
            });
        });
        return map;
    }, [optionsData]);

    // Id lookups, indexed once. The coverage/candidate code below runs per pedido
    // line while the drawer is open — with ~150 lines, a linear `find` over the
    // ~300-product catalogue and the ~50-machine list inside those loops is the
    // difference between a scan and a stall.
    const machineById = useMemo(() => {
        const map = new Map<number, MachineOption>();
        machines.forEach((machine) => map.set(machine.id, machine));
        return map;
    }, [machines]);

    const productById = useMemo(() => {
        const map = new Map<number, ProductOption>();
        products.forEach((product) => map.set(product.id, product));
        return map;
    }, [products]);

    // Label lookups. Declared here rather than further down because the
    // coverage callbacks below list machineName in their dependency arrays, and
    // a dep array is evaluated during render — a `const` declared later would
    // throw on the temporal dead zone, not merely read oddly.
    const machineName = useCallback(
        (machineId: number | null) =>
            (machineId ? machineById.get(machineId)?.name : '') || '',
        [machineById],
    );
    const productDescription = (productId: number | null) =>
        (productId ? productById.get(productId)?.compound_description : '') ||
        '';
    const employeeName = (employeeId: number) =>
        employees.find((employee) => employee.id === employeeId)?.fullname ||
        '';

    // The row callbacks are all stable (functional state updates, empty deps) so
    // they don't hand every row a new prop on each render. With the accordion
    // only one row mounts inputs at a time, but the collapsed summaries still
    // re-render on every keystroke and there is no reason to make that cost more
    // than it must.
    const handleRowChange = useCallback(
        (index: number, patch: Partial<PlanRowState>) => {
            const normalizedPatch: Partial<PlanRowState> = {
                ...patch,
                ...(patch.shift_hours !== undefined
                    ? {
                          shift_hours:
                              patch.shift_hours === null
                                  ? null
                                  : normalizeHours(patch.shift_hours),
                      }
                    : {}),
                ...(patch.products
                    ? {
                          products: patch.products.map((product) => ({
                              ...product,
                              hours: normalizeHours(product.hours),
                          })),
                      }
                    : {}),
            };
            setRows((prev) =>
                prev.map((row, i) =>
                    i === index ? { ...row, ...normalizedPatch } : row,
                ),
            );
            setDirty(true);
        },
        [],
    );

    // Whether getProductsInventory returns a real balance for this product's
    // production type. The type-id rule lives in the shared isInventoryTracked
    // helper so this page, the drawer, orders-to-fulfill and products-to-produce
    // can never disagree. Here we only resolve the id → product's type.
    const isInventoryTracked = useCallback(
        (productId: number | null) => {
            if (!productId) return false;
            const product = productById.get(productId);
            return isInventoryTrackedForType(
                product?.order_production_type_id ?? null,
            );
        },
        [productById],
    );

    // The rate a given machine would run a given product at, and whether it can
    // be trusted yet. The wait-for-the-pair rule lives in resolveRateState, so
    // this page, the product line's caption and the collapsed row summary can
    // never quote different numbers for the same pair.
    //
    // Everything downstream — the planned totals, the coverage the drawer
    // allocates, the picker's ranking and the hours a click assigns — reads the
    // rate from here, which is what makes them consistent (REV-10).
    const rateFor = useCallback(
        (machineId: number | null, productId: number | null): RateState => {
            if (!machineId) {
                return {
                    status: 'none',
                    value: null,
                    source: 'none',
                    runCount: 0,
                    productRunCount: 0,
                };
            }
            return resolveRateState({
                productId: productId ?? null,
                productRate: productId
                    ? getRate(machineId, productId)
                    : undefined,
                machineRate: getRate(machineId, null),
            });
        },
        [getRate],
    );

    // ALL rate fetching for the plan lives here, on the page.
    //
    // It used to live on the row, which was fine while every row was mounted at
    // all times (collapsed rows still rendered). The WhatsApp tab broke that:
    // rows unmount when it is open, so a planner who switched tabs and then
    // opened Pedidos por surtir got a coverage panel stuck on "calculando",
    // because nothing left on screen was asking for the corridas it needs.
    //
    // Two series per row: the machine's overall corridas (the fallback rate, and
    // what the picker ranks a freshly seeded plan by) and each planned pair,
    // which is what plannedKilosByProduct — and therefore all of coverage — is
    // actually built from. Real production is displayed separately.
    const ratePairsKey = rows
        .map((row) =>
            row.machine_id
                ? `${row.machine_id}:${row.products
                      .map((rowProduct) => rowProduct.product_id ?? '')
                      .join('|')}`
                : '',
        )
        .join(',');
    useEffect(() => {
        // Parsed back out of the digest rather than read from `rows`, so the
        // effect depends on the pairs themselves and not on every note or
        // employee edit that gives `rows` a new identity.
        const pairs: RatePair[] = [];
        ratePairsKey
            .split(',')
            .filter((part) => part.length > 0)
            .forEach((part) => {
                const [machinePart, productsPart] = part.split(':');
                const machineId = Number(machinePart);
                pairs.push({ machineId, productId: null });
                (productsPart || '')
                    .split('|')
                    .filter((id) => id.length > 0)
                    .forEach((id) =>
                        pairs.push({
                            machineId,
                            productId: Number(id),
                        }),
                    );
            });
        ensureRates(pairs);
    }, [ratePairsKey, ensureRates]);

    // Machines the plan on screen actually uses. Live rows, not the saved plan,
    // so removing a machine re-nets immediately.
    // Real production for this slot, keyed both ways: per product for the
    // netting below, per machine+product for each row's planned-vs-real caption.
    //
    // The per-product total counts ONLY the plan's own machines. The query
    // returns everything captured in the date/turno/sucursal, which is the right
    // scope for a query with no plan to filter by — but the netting below asks a
    // narrower question: how much of THIS plan's forecast has already happened?
    // Production on a machine the plan does not include was never part of that
    // forecast, and it is already inside the inventory the forecast is added to,
    // so counting it here subtracts it twice and understates what the turno
    // still has to make (REV-4).
    const actualByMachineProduct = useMemo(() => {
        const totals = new Map<string, { kilos: number; groups: number }>();
        (actualsData?.getProductionPlanActuals || []).forEach((actual) => {
            if (!actual.product_id || !actual.machine_id) return;
            const key = `${actual.machine_id}:${actual.product_id}`;
            const current = totals.get(key) || { kilos: 0, groups: 0 };
            current.kilos += actual.kilos || 0;
            current.groups += actual.groups || 0;
            totals.set(key, current);
        });
        return totals;
    }, [actualsData]);

    // Product-level actuals for the pedido drawer. Rows keep the precise
    // machine+product values above; pedido lines have no machine link, so their
    // real value is the product total for the selected slot.
    const actualByProduct = useMemo(() => {
        const totals = new Map<number, { kilos: number; groups: number }>();
        (actualsData?.getProductionPlanActuals || []).forEach((actual) => {
            if (!actual.product_id) return;
            const current = totals.get(actual.product_id) || {
                kilos: 0,
                groups: 0,
            };
            current.kilos += actual.kilos || 0;
            current.groups += actual.groups || 0;
            totals.set(actual.product_id, current);
        });
        return totals;
    }, [actualsData]);

    // What the plan ON SCREEN would produce, per product. Built from the live
    // edited rows (not the saved plan) so the drawer reacts as the planner
    // types — the whole reason it lives on this page.
    const plannedKilosByProduct = useMemo(() => {
        const totals = new Map<number, number>();
        rows.forEach((row) => {
            row.products.forEach((rowProduct) => {
                if (!rowProduct.product_id || rowProduct.hours <= 0) return;
                const rate = rateFor(row.machine_id, rowProduct.product_id);
                // Pending pairs contribute nothing rather than a machine
                // average that is about to be replaced. The figures they feed
                // are labelled as still loading (ratesPending) instead of being
                // shown as final and then moving.
                if (rate.status !== 'ready' || rate.value === null) return;
                // Scaled by the line's own efficiency — this total is what the
                // drawer's coverage is allocated from, so it has to be the same
                // number the planner reads on the line itself.
                totals.set(
                    rowProduct.product_id,
                    (totals.get(rowProduct.product_id) || 0) +
                        effectiveKgHr(rate.value, rowProduct.efficiency) *
                            rowProduct.hours,
                );
            });
        });
        return totals;
    }, [rows, rateFor]);

    // True while any planned product is still waiting for its machine x product
    // corridas. Every figure derived from the rates — the coverage in the
    // drawer, the collapsed row summaries — is incomplete until this clears, so
    // they say "calculando" instead of showing a number that would then jump.
    // The rows fetch their pairs as soon as they mount, collapsed included, so
    // this is normally one round trip after the plan loads.
    const ratesPending = useMemo(
        () =>
            rows.some((row) =>
                row.products.some(
                    (rowProduct) =>
                        !!rowProduct.product_id &&
                        rateFor(row.machine_id, rowProduct.product_id)
                            .status === 'pending',
                ),
            ),
        [rows, rateFor],
    );

    // Keep the plan-wide forecast used by coverage, and retain the machine-row
    // pieces for the tooltip. Captured production is shown separately and does
    // not reduce either the forecast or the allocation pool.
    const plannedProductContributions = useMemo(() => {
        const byProduct = new Map<
            number,
            PlannedProductContribution[]
        >();
        rows.forEach((row) => {
            const name = machineName(row.machine_id);
            if (!name) return;
            row.products.forEach((rowProduct) => {
                if (!rowProduct.product_id || rowProduct.hours <= 0) return;
                const rate = rateFor(row.machine_id, rowProduct.product_id);
                if (rate.status !== 'ready' || rate.value === null) return;
                const kilos =
                    effectiveKgHr(rate.value, rowProduct.efficiency) *
                    rowProduct.hours;
                const contributions =
                    byProduct.get(rowProduct.product_id) || [];
                contributions.push({ machineName: name, kilos });
                byProduct.set(rowProduct.product_id, contributions);
            });
        });

        return byProduct;
    }, [rows, rateFor, machineName]);

    // Allocation runs over the WHOLE pending queue, both statuses, always.
    //
    // The status toggle is a display filter and nothing more (D14/D18). Filtering
    // before this call would change the arithmetic, not the view: the allocation
    // is a priority-consuming pool, so dropping the Pendiente pedidos out of the
    // walk means they never take their share and the committed ones are served
    // from a pool that is too big. 100 kg on hand, pedido A (Pendiente, priority
    // 1) wants 80, pedido B (En producción) wants 60 — filtered, B reads
    // "cubierto con inventario" off stock that A is ahead of it for. Pedidos por
    // surtir walks every row the query returns and only draws a line between the
    // two buckets when rendering; this has to do the same or the two boards
    // disagree, and §10.2's cross-check silently stops applying to the default
    // view.
    //
    // Still only computed while the drawer is open: it depends on
    // plannedKilosByProduct, which changes on every edit, so leaving it always-on
    // meant re-walking the queue on each keystroke for something nobody was
    // looking at. Opening the drawer recomputes from the live rows anyway.
    const coveragePedidos = useMemo(
        () =>
            pedidosOpen
                ? computeCoverage({
                      demandRows: optionsData?.getOptimizedRequestProducts || [],
                      inventory,
                      // The raw plan forecast. Real production is displayed
                      // separately and does not reduce it.
                      plannedKilosByProduct,
                      actualByProduct,
                  })
                : [],
        [
            pedidosOpen,
            optionsData,
            inventory,
            plannedKilosByProduct,
            actualByProduct,
        ],
    );

    // What the drawer actually renders. The pedidos filtered out here have
    // already consumed their share of the stock above, which is the point.
    const visiblePedidos = useMemo(
        () =>
            pedidosIncludePending
                ? coveragePedidos
                : coveragePedidos.filter(
                      (pedido) =>
                          pedido.order_request_status_id === IN_PRODUCTION,
                  ),
        [coveragePedidos, pedidosIncludePending],
    );

    // Sized from what is ON SCREEN, not the full queue: the planner narrowed the
    // list to the pedidos they are planning against, and a click that quietly
    // bought hours for pedidos they excluded would produce a need figure that
    // ties to nothing visible. This also includes the product's negative stock
    // debt, while already-planned output beyond pedido demand pays that debt
    // down. (REV-11 revisits the per-line vs per-product half of this question.)
    const productionNeededByProduct = useMemo(
        () =>
            productionNeededKilosByProduct(
                visiblePedidos,
                inventory,
                plannedKilosByProduct,
            ),
        [visiblePedidos, inventory, plannedKilosByProduct],
    );

    // Both the candidate ranking and the click's hours arithmetic live in
    // plan-allocation.ts — no React, so the rules can be read and tested on
    // their own instead of only by clicking through the drawer.
    // Whether a machine has run a product in the last 12 months, read from the
    // same cache the row's product picker groups by — so "Producidos en esta
    // máquina el último año" in the row and "ya lo ha producido" in the picker
    // are one fact, not two that can drift apart. Undefined until the machine's
    // history lands.
    const hasProducedProduct = useCallback(
        (machineId: number, productId: number): boolean | undefined => {
            const produced = getMachineProduct(machineId);
            if (produced === undefined) return undefined;
            return produced.includes(productId);
        },
        [getMachineProduct],
    );

    const candidateRowsFor = useCallback(
        (productId: number) =>
            candidateRows({
                rows,
                productId,
                machineById,
                productById,
                planShiftHours: shiftHours,
                rateFor,
                hasProducedProduct,
            }),
        [
            rows,
            productById,
            machineById,
            shiftHours,
            rateFor,
            hasProducedProduct,
        ],
    );

    // Per-product state the drawer renders: where it is already planned, and
    // whether a click can land right now.
    //
    // Built ONCE for the products actually on screen, not resolved per line.
    // Each entry walks every row, so calling it inside the drawer's render meant
    // ~150 lines × ~20 rows on every keystroke — and the drawer is meant to stay
    // open while the plan is edited behind it, which is exactly when that ran.
    const productPlanStates = useMemo(() => {
        const states = new Map<number, ProductPlanState>();
        if (!pedidosOpen) return states;
        visiblePedidos.forEach((pedido) => {
            pedido.lines.forEach((line) => {
                const productId = line.product_id;
                if (!productId || states.has(productId)) return;
                const planned = rows
                    .filter((row) =>
                        row.products.some((p) => p.product_id === productId),
                    )
                    .map((row) => machineName(row.machine_id))
                    .filter((name) => name.length > 0);
                const { candidates, reason } = candidateRowsFor(productId);
                states.set(productId, {
                    machines: planned,
                    canAdd: candidates.some(
                        (candidate) =>
                            candidate.compatible && candidate.hasFreeHours,
                    ),
                    reason,
                });
            });
        });
        return states;
    }, [pedidosOpen, visiblePedidos, rows, machineName, candidateRowsFor]);

    // Opening the picker fetches this product's corridas on EVERY machine in the
    // plan, because that is what the list ranks by and what the click's hours
    // are derived from. A product being planned for the first time has no pair
    // cached anywhere — the rows only fetch the pairs they already run — and
    // before this the ranking silently used machine averages instead (off by a
    // median 17%, p90 58%). One query per machine, once per product, while a
    // Missing rates join the same batch request while the modal is open and
    // nobody is typing.
    const pickerMachineIdsKey = rows
        .map((row) => row.machine_id ?? '')
        .join(',');
    useEffect(() => {
        if (machinePickerProductId === null) return;
        pickerMachineIdsKey
            .split(',')
            .filter((id) => id.length > 0)
            .forEach((id) => {
                // Each machine's produced-products list, for the "ya lo ha
                // producido" chip. A separate question from the rate: a rate
                // needs kilos AND hours captured, so a machine that ran the
                // product before hourly capture has history here and none
                // there. Cached per machine and shared with the row's product
                // picker, so an expanded row has usually paid for it already.
                ensureMachineProduct(Number(id));
            });
        ensureRates(
            pickerMachineIdsKey
                .split(',')
                .filter((id) => id.length > 0)
                .flatMap((id): RatePair[] => [
                    { machineId: Number(id), productId: null },
                    {
                        machineId: Number(id),
                        productId: machinePickerProductId,
                    },
                ]),
        );
        // pickerMachineIdsKey is a primitive digest of the machine ids so the
        // effect doesn't re-run on every unrelated row edit.
    }, [
        machinePickerProductId,
        pickerMachineIdsKey,
        ensureRates,
        ensureMachineProduct,
    ]);

    // The open picker's list. Memoized because candidateRowsFor walks every row,
    // and rendering it inline re-ran that on every page render — including the
    // keystrokes of the very hours field the dialog is about to change.
    const machinePickerCandidates = useMemo(
        () =>
            machinePickerProductId
                ? candidateRowsFor(machinePickerProductId).candidates
                : [],
        [machinePickerProductId, candidateRowsFor],
    );

    // Clicking a pedido product opens the machine picker. Merging is BY PRODUCT —
    // with 79% of products wanted by several pedidos at once, clicking the same
    // product from two cards must not create two plans of it.
    const handleAddFromPedido = (productId: number) => {
        if (!canEdit) return;
        const { candidates, reason } = candidateRowsFor(productId);
        // Only when the plan has no machines at all is there nothing to show.
        // Otherwise open the picker even if nothing is selectable: the list of
        // machines with their reasons ("otro tipo de producción", "sin horas
        // libres") answers more than a snackbar can.
        if (candidates.length === 0) {
            dispatch(
                pushMessage({
                    message: reason || 'No hay máquina disponible',
                    options: { variant: 'warning' },
                }),
            );
            return;
        }
        setMachinePickerProductId(productId);
    };

    const handlePickMachine = (rowIndex: number) => {
        const productId = machinePickerProductId;
        setMachinePickerProductId(null);
        if (productId === null) return;

        const row = rows[rowIndex];
        if (!row) return;

        // The efficiency the resulting line will carry: the existing line's when
        // this click tops one up, the default when it opens a new one. Buying
        // hours at a different figure than the line then predicts at would leave
        // the click's own snackbar disagreeing with the row it just edited.
        const existingLine = row.products.find(
            (rowProduct) => rowProduct.product_id === productId,
        );
        const efficiencyPercent =
            existingLine?.efficiency ?? DEFAULT_EFFICIENCY;

        // All of the arithmetic — and the refusals — are in plan-allocation.ts.
        const outcome = planClickHours({
            uncoveredKilos: productionNeededByProduct.get(productId) || 0,
            rate: rateFor(row.machine_id, productId),
            efficiencyPercent,
            freeHours: freeHoursOf(row, shiftHours),
        });

        // A refused click changes nothing and says why. The picker already
        // disables these rows, so reaching this is either a race (a rate landing
        // between render and click) or a state the list could not foresee — and
        // silently assigning hours on a guess is exactly what must not happen.
        if (outcome.refusal) {
            dispatch(
                pushMessage({
                    message: CLICK_REFUSAL_MESSAGE[outcome.refusal],
                    options: { variant: 'warning' },
                }),
            );
            return;
        }

        const { products: nextProducts, merged } = addProductHours({
            products: row.products,
            productId,
            hours: normalizeHours(outcome.assigned),
            createLine: (id, hours) => ({
                key: nextRowProductKey(),
                id: null,
                product_id: id,
                hours,
                efficiency: efficiencyPercent,
            }),
        });

        handleRowChange(rowIndex, { products: nextProducts });

        // The drawer covers the plan, so confirm WHERE it landed and how much —
        // otherwise a click looks like it did nothing.
        const parts = [
            `${merged ? 'Sumado a' : 'Agregado a'} ${machineName(
                row.machine_id,
            )}`,
            `${formatFloat(outcome.assigned, 1)} h`,
        ];
        if (outcome.shortfall > HOURS_EPSILON) {
            parts.push(
                `faltan ${formatFloat(
                    outcome.shortfall,
                    1,
                )} h para cubrir lo que queda de este producto`,
            );
        }
        dispatch(
            pushMessage({
                message: parts.join(' — '),
                options: {
                    variant:
                        outcome.shortfall > HOURS_EPSILON ? 'info' : 'success',
                },
            }),
        );
    };

    const handleAddRow = useCallback(() => {
        const key = nextRowKey();
        setRows((prev) => [
            ...prev,
            {
                key,
                id: null,
                machine_id: null,
                shift_hours: null,
                products: [],
                notes: [],
                employee_ids: [],
            },
        ]);
        // A new row has nothing to summarise, so open it straight away —
        // otherwise "Agregar máquina" appears to add a blank line and do nothing.
        setExpandedRowKey(key);
        setDirty(true);
    }, []);

    const handleToggleExpand = useCallback((key: string) => {
        setExpandedRowKey((current) => (current === key ? null : key));
    }, []);

    const handleRemoveRow = useCallback((index: number) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
        setDirty(true);
    }, []);

    const handleMoveRow = useCallback(
        (index: number, direction: 'up' | 'down') => {
            const target = direction === 'up' ? index - 1 : index + 1;
            if (target < 0) return;
            setRows((prev) => {
                // Bounds-checked inside the updater so the callback doesn't need
                // `rows` as a dependency (which would change every edit).
                if (target >= prev.length) return prev;
                const next = [...prev];
                const [moved] = next.splice(index, 1);
                next.splice(target, 0, moved);
                return next;
            });
            setDirty(true);
        },
        [],
    );

    // product id → the machines planning it, across the whole plan.
    //
    // Feeds the asterisk on a line's `final` projection. That figure is per
    // PRODUCT, not per line (D13/REV-3) — two machines making the same bolsa
    // draw from one warehouse, so computing it per line made each row re-add the
    // same available stock. The consequence is that on those rows `Disp + ≈` no
    // longer equals `final`, and until now nothing on screen said why.
    const plannedMachinesByProduct = useMemo(() => {
        const byProduct = new Map<number, string[]>();
        rows.forEach((row) => {
            const name = machineName(row.machine_id);
            if (!name) return;
            row.products.forEach((rowProduct) => {
                if (!rowProduct.product_id) return;
                const names = byProduct.get(rowProduct.product_id) || [];
                // Deduped by name: the same machine on two rows is a plan the
                // planner will fix, not a second contributor to name twice.
                if (!names.includes(name)) names.push(name);
                byProduct.set(rowProduct.product_id, names);
            });
        });
        return byProduct;
    }, [rows, machineName]);

    // employee id → how many plan rows already reference it. Passed to each
    // row's employee picker so already-assigned people show highlighted right
    // in the dropdown (replacing the old read-only badge strip): a quick
    // "who's taken" glance at the point of assignment.
    const employeeUsage = useMemo(() => {
        const counts = new Map<string | number, number>();
        rows.forEach((row) => {
            row.employee_ids.forEach((id) => {
                counts.set(id, (counts.get(id) ?? 0) + 1);
            });
        });
        return counts;
    }, [rows]);

    // Guards a header-selector change: if there are unsaved edits, confirm
    // before applying (the reset effect wipes rows when date/turno/sucursal/
    // tipo change). Declining leaves the selector untouched.
    const guardedHeaderChange = (apply: () => void) => {
        if (dirty && !window.confirm(DISCARD_CONFIRM)) return;
        apply();
    };

    const handleSave = async () => {
        if (!canEdit) return;

        // Saving refreshes the plan data, but it must not move the planner to a
        // different header selection. Keep the exact selection from the click
        // while the mutation/refetch completes; production type is a local
        // machine filter and is intentionally not part of the plan payload.
        const headerSelection = {
            date,
            shift,
            branchId,
            productionTypeId,
        };

        if (rows.some((row) => !row.machine_id)) {
            dispatch(
                pushMessage({
                    message: 'Cada renglón necesita una máquina',
                    options: { variant: 'error' },
                }),
            );
            return;
        }

        if (rows.some((row) => row.products.some((p) => !p.product_id))) {
            dispatch(
                pushMessage({
                    message: 'Cada producto planeado necesita un producto',
                    options: { variant: 'error' },
                }),
            );
            return;
        }

        // Checked here so the message says what the field means, rather than
        // surfacing the backend's positional `product 123: efficiency must be…`.
        if (
            rows.some((row) =>
                row.products.some(
                    (p) => !(p.efficiency > 0 && p.efficiency <= 100),
                ),
            )
        ) {
            dispatch(
                pushMessage({
                    message: 'La eficiencia debe estar entre 1 y 100%',
                    options: { variant: 'error' },
                }),
            );
            return;
        }

        // The sum rule, checked here so the message can NAME the machines rather
        // than surfacing the backend's generic row-position error. Measured
        // against each row's own turno length, so a machine running a short
        // shift is judged against its six hours, not the plan's eight. Rows with
        // no products are exempt — the fresh seed creates exactly those.
        const unbalanced = rows.filter((row) => {
            if (row.products.length === 0) return false;
            const target = effectiveShiftHours(row.shift_hours, shiftHours);
            const assigned = row.products.reduce(
                (sum, p) => sum + (p.hours || 0),
                0,
            );
            return Math.abs(assigned - target) > HOURS_EPSILON;
        });
        if (unbalanced.length > 0) {
            // Each row can now have a different target, so the message names it
            // per machine instead of asserting one number for the whole plan.
            const names = unbalanced
                .map((row) => {
                    const name = machineName(row.machine_id);
                    if (!name) return '';
                    return `${name} (${formatFloat(
                        effectiveShiftHours(row.shift_hours, shiftHours),
                        1,
                    )} h)`;
                })
                .filter((name) => name.length > 0)
                .join(', ');
            dispatch(
                pushMessage({
                    message: `Las horas no suman en: ${names}`,
                    options: { variant: 'error' },
                }),
            );
            return;
        }

        // A product may appear at most once per row (same rule as a sale).
        const duplicated = rows.filter((row) => {
            const ids = row.products
                .map((p) => p.product_id)
                .filter((id): id is number => !!id);
            return new Set(ids).size !== ids.length;
        });
        if (duplicated.length > 0) {
            const names = duplicated
                .map((row) => machineName(row.machine_id))
                .filter((name) => name.length > 0)
                .join(', ');
            dispatch(
                pushMessage({
                    message: `${names}: hay un producto repetido en el mismo renglón`,
                    options: { variant: 'error' },
                }),
            );
            return;
        }

        try {
            await upsertPlan({
                variables: {
                    ProductionPlanUpsertInput: {
                        id: planId,
                        date: getStringFromDate(date),
                        shift,
                        shift_hours: shiftHours,
                        branch_id: branchId,
                        notes: joinNotes(pairNotes),
                        product_notes: joinNotes(productNotes),
                        rows: rows.map((row, index) => ({
                            id: row.id,
                            machine_id: row.machine_id,
                            shift_hours: row.shift_hours,
                            notes: joinNotes(row.notes),
                            position: index,
                            employee_ids: row.employee_ids,
                            products: row.products.map(
                                (rowProduct, productIndex) => ({
                                    id: rowProduct.id,
                                    product_id: rowProduct.product_id,
                                    hours: normalizeHours(rowProduct.hours),
                                    efficiency: rowProduct.efficiency,
                                    position: productIndex,
                                }),
                            ),
                        })),
                    },
                },
            });
            await refetchPlan({
                date: getStringFromDate(headerSelection.date),
                shift: headerSelection.shift,
                branchId: headerSelection.branchId,
            });
            setDate(headerSelection.date);
            setShift(headerSelection.shift);
            setBranchId(headerSelection.branchId);
            setProductionTypeId(headerSelection.productionTypeId);
            dispatch(
                pushMessage({
                    message: 'Planeación guardada',
                    options: { variant: 'success' },
                }),
            );
        } catch (e) {
            dispatch(
                pushMessage({
                    message: 'No se pudo guardar la planeación',
                    options: { variant: 'error' },
                }),
            );
        }
    };

    const handleDeletePlan = async () => {
        if (!canEdit || !planId) return;
        if (
            !window.confirm(
                '¿Eliminar esta planeación? No se puede deshacer.',
            )
        ) {
            return;
        }
        try {
            await deletePlan({
                variables: { ProductionPlanId: planId },
            });
            // The plan is gone; refetch returns null and the reset effect
            // reseeds the editor from the machine list.
            await refetchPlan();
            dispatch(
                pushMessage({
                    message: 'Planeación eliminada',
                    options: { variant: 'success' },
                }),
            );
        } catch (e) {
            dispatch(
                pushMessage({
                    message: 'No se pudo eliminar la planeación',
                    options: { variant: 'error' },
                }),
            );
        }
    };

    const dayName = () =>
        dayjs(date).locale('es').format('dddd').toUpperCase();
    const turnoLabel = () => TURNO_ORDINAL[shift] || `${shift}`;

    // Row notes joined onto a single line for the WhatsApp instructions.
    const rowNotesInline = (items: string[]): string =>
        items
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
            .join('; ');

    // "Copiar parejas": employee distribution. Header + one machine/crew line
    // per row, then the parejas notes as trailing bullets when present.
    const buildPairsText = (): string => {
        const header = `Les paso parejas para arrancar ${turnoLabel()} turno: ${dayName()}`;
        const pairLines = rows.map((row) => {
            const names = row.employee_ids
                .map((id) => employeeName(id))
                .filter((name) => name.length > 0);
            return `${machineName(row.machine_id)}: ${names.join(' y ')}`;
        });
        const sections = [header, '', pairLines.join('\n')];
        if (pairNotes.some((note) => note.trim().length > 0)) {
            sections.push(
                '',
                pairNotes
                    .map((note) => note.trim())
                    .filter((note) => note.length > 0)
                    .map((note) => `- ${note}`)
                    .join('\n'),
            );
        }
        return sections.join('\n');
    };

    // Products on one row, in planned order, each with its slice of the turno.
    // Hours are included because they are the instruction now — with two products
    // on one machine, "saquen X y Y" alone doesn't say how to divide the shift.
    // A row that runs a single product for the whole turno omits the hours: they
    // add nothing there and would just make every line noisier.
    const rowProductsInline = (row: PlanRowState): string => {
        const parts = row.products
            .filter((rowProduct) => !!rowProduct.product_id)
            .map((rowProduct) => {
                const description = productDescription(rowProduct.product_id);
                if (row.products.length === 1) return description;
                return `${description} (${formatFloat(
                    rowProduct.hours,
                    1,
                )} h)`;
            });
        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0];
        // "A, B y C" — the Spanish list separator, not a trailing comma.
        return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
    };

    // "Copiar productos": product distribution / instructions. The header is a
    // proposal ("Instrucciones …") — the user adjusts it at review before
    // sending. Instruction lines, then the productos notes as trailing bullets.
    const buildProductsText = (): string => {
        const header = `Instrucciones ${turnoLabel()} turno: ${dayName()}`;
        const instructionLines = rows
            .map((row) => {
                const productsInline = rowProductsInline(row);
                const product = productsInline
                    ? `saquen ${productsInline}`
                    : '';
                const rowNotes = rowNotesInline(row.notes);
                if (!product && !rowNotes) return null;
                let line = `${machineName(row.machine_id)}: `;
                if (product && rowNotes) {
                    line += `${product} — ${rowNotes}`;
                } else {
                    line += product || rowNotes;
                }
                return line;
            })
            .filter((line): line is string => line !== null);

        const sections = [header, '', instructionLines.join('\n')];
        if (productNotes.some((note) => note.trim().length > 0)) {
            sections.push(
                '',
                productNotes
                    .map((note) => note.trim())
                    .filter((note) => note.length > 0)
                    .map((note) => `- ${note}`)
                    .join('\n'),
            );
        }
        return sections.join('\n');
    };

    // True when the productos message would have any content beyond its header.
    const hasProductsContent =
        rows.some(
            (row) =>
                row.products.some((rowProduct) => !!rowProduct.product_id) ||
                rowNotesInline(row.notes).length > 0,
        ) || productNotes.some((note) => note.trim().length > 0);

    const handleCopyPairs = async () => {
        const ok = await copyToClipboard(buildPairsText());
        dispatch(
            pushMessage({
                message: ok ? 'Parejas copiadas' : 'No se pudo copiar',
                options: { variant: ok ? 'success' : 'error' },
            }),
        );
    };

    const handleCopyProducts = async () => {
        const ok = await copyToClipboard(buildProductsText());
        dispatch(
            pushMessage({
                message: ok ? 'Productos copiados' : 'No se pudo copiar',
                options: { variant: ok ? 'success' : 'error' },
            }),
        );
    };

    // --- Import "Copiar de otro turno…" -----------------------------------
    const [importOpen, setImportOpen] = useState(false);
    const [importDate, setImportDate] = useState<string>(date);
    const [importShift, setImportShift] = useState<number>(1);
    const [importBranchId, setImportBranchId] = useState<number | null>(null);
    const [importPreview, setImportPreview] = useState<{
        loading: boolean;
        fetched: boolean;
        rows: SourcePlanRow[];
        // Plan-level notes of the source plan, copied alongside the rows.
        notes: string;
        product_notes: string;
        // Copied too: the imported rows' hours were split against the SOURCE
        // plan's shift, so importing without it would break the sum rule on
        // arrival for any row whose turno length differs.
        shift_hours: number;
    }>({
        loading: false,
        fetched: false,
        rows: [],
        notes: '',
        product_notes: '',
        shift_hours: DEFAULT_SHIFT_HOURS,
    });

    const openImport = () => {
        // Default to the previous day + the current turno/sucursal — the most
        // common "copy yesterday's same turno" case.
        setImportDate(dayjs(date).subtract(1, 'day').format(DATE_FORMAT));
        setImportShift(shift);
        setImportBranchId(branchId);
        setImportPreview({
            loading: false,
            fetched: false,
            rows: [],
            notes: '',
            product_notes: '',
            shift_hours: DEFAULT_SHIFT_HOURS,
        });
        setImportOpen(true);
    };

    // Fetch the source plan whenever the modal is open and its selectors
    // change, via the existing ProductionPlanForDate document (network-only so
    // the preview reflects the latest saved plan). `cancelled` drops stale
    // responses if the user changes a field mid-flight.
    useEffect(() => {
        if (!importOpen || !importDate) return;
        let cancelled = false;
        setImportPreview({
            loading: true,
            fetched: false,
            rows: [],
            notes: '',
            product_notes: '',
            shift_hours: DEFAULT_SHIFT_HOURS,
        });
        client
            .query<
                ProductionPlanForDateQuery,
                ProductionPlanForDateQueryVariables
            >({
                query: ProductionPlanForDateDocument,
                variables: {
                    date: getStringFromDate(importDate),
                    shift: importShift,
                    branchId: importBranchId,
                },
                fetchPolicy: 'network-only',
            })
            .then((res) => {
                if (cancelled) return;
                const plan = res.data?.getProductionPlan;
                setImportPreview({
                    loading: false,
                    fetched: true,
                    rows: plan ? plan.rows : [],
                    notes: plan?.notes ?? '',
                    product_notes: plan?.product_notes ?? '',
                    shift_hours: normalizeHours(
                        plan?.shift_hours || DEFAULT_SHIFT_HOURS,
                    ),
                });
            })
            .catch(() => {
                if (cancelled) return;
                setImportPreview({
                    loading: false,
                    fetched: true,
                    rows: [],
                    notes: '',
                    product_notes: '',
                    shift_hours: DEFAULT_SHIFT_HOURS,
                });
            });
        return () => {
            cancelled = true;
        };
    }, [importOpen, importDate, importShift, importBranchId, client]);

    const handleImport = () => {
        const sourceRows = importPreview.rows;
        if (sourceRows.length === 0) return;
        if (dirty && !window.confirm(DISCARD_CONFIRM)) return;
        // Map to fresh local rows: new keys and id:null so saving creates them
        // under the CURRENT plan (date/turno/sucursal), not the source plan.
        setRows(
            sourceRows.map((sourceRow) => ({
                key: nextRowKey(),
                id: null,
                machine_id: sourceRow.machine_id ?? null,
                // Copied with the hours: a row whose machine ran a short turno
                // has products split against THAT length, so dropping the
                // override would break the sum rule the moment they land.
                shift_hours:
                    sourceRow.shift_hours == null
                        ? null
                        : normalizeHours(sourceRow.shift_hours),
                products: sourceRow.products.map((sourceProduct) => ({
                    key: nextRowProductKey(),
                    id: null,
                    product_id: sourceProduct.product_id ?? null,
                    hours: normalizeHours(sourceProduct.hours ?? 0),
                    // Copied with the line: the source turno's efficiency is a
                    // judgement about that machine running that product, which
                    // is exactly what is being copied forward.
                    efficiency:
                        sourceProduct.efficiency ?? DEFAULT_EFFICIENCY,
                })),
                notes: splitNotes(sourceRow.notes),
                employee_ids: sourceRow.employees.map(
                    (employee) => employee.id,
                ),
            })),
        );
        // Import copies BOTH plan-level lists from the source plan, and its
        // shift length — the copied hours were split against that shift.
        setPairNotes(splitNotes(importPreview.notes));
        setProductNotes(splitNotes(importPreview.product_notes));
        setShiftHours(normalizeHours(importPreview.shift_hours));
        setDirty(true);
        setImportOpen(false);
    };

    const importPreviewLine = importPreview.loading
        ? 'Buscando…'
        : !importPreview.fetched
        ? ''
        : importPreview.rows.length > 0
        ? `${importPreview.rows.length} renglones`
        : 'No hay planeación para esa combinación';

    return (
        <DPage>
            <DPageHeader title={'Planeación'}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {canEdit && planId ? (
                        <Button
                            variant={'outlined'}
                            color={'error'}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={handleDeletePlan}
                            disabled={deleting}
                        >
                            {deleting ? 'Eliminando...' : 'Eliminar'}
                        </Button>
                    ) : null}
                    {/* Sits between Eliminar and Guardar: importing is a
                        plan-level action on the whole plan, like those two, not
                        a filter like the selectors below. */}
                    {canEdit ? (
                        <Button
                            variant={'outlined'}
                            startIcon={<ImportExportIcon />}
                            onClick={openImport}
                        >
                            Copiar de otro turno…
                        </Button>
                    ) : null}
                    {canEdit ? (
                        <Button
                            variant={'contained'}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    ) : null}
                </Box>
            </DPageHeader>
            <DPageBody disableGutters>
                <Box
                    sx={{
                        // pt so the outlined inputs' floating labels aren't
                        // clipped by the scroll container's top edge.
                        pt: 2,
                        px: 2,
                        pb: 2,
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                    }}
                >
                    {/* Header selectors + actions. Every control carries no
                        external margin (date picker: disableGutters; selects:
                        margin none) and the row bottom-aligns them, so the
                        fields line up on one baseline instead of overlapping. */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'flex-end',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ width: 180 }}>
                            <DDatePicker
                                views={['day']}
                                inputFormat={DATE_FORMAT}
                                label={'Fecha'}
                                value={date}
                                clearable={false}
                                disableGutters
                                onChange={(val) => {
                                    if (val)
                                        guardedHeaderChange(() => setDate(val));
                                }}
                            />
                        </Box>
                        <TextField
                            select
                            label={'Turno'}
                            size={'small'}
                            margin={'none'}
                            value={shift}
                            onChange={(e) =>
                                guardedHeaderChange(() =>
                                    setShift(Number(e.target.value)),
                                )
                            }
                            sx={{ width: 100 }}
                        >
                            {SHIFTS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={'Sucursal'}
                            size={'small'}
                            margin={'none'}
                            value={branchId ?? ''}
                            onChange={(e) =>
                                guardedHeaderChange(() =>
                                    setBranchId(Number(e.target.value)),
                                )
                            }
                            sx={{ width: 150 }}
                        >
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={'Tipo de producción'}
                            size={'small'}
                            margin={'none'}
                            value={productionTypeId ?? ''}
                            onChange={(e) =>
                                guardedHeaderChange(() =>
                                    setProductionTypeId(Number(e.target.value)),
                                )
                            }
                            sx={{ width: 190 }}
                        >
                            {productionTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type={'number'}
                            label={'Horas'}
                            size={'small'}
                            margin={'none'}
                            value={shiftHours}
                            // Unlike the other header selectors this does NOT
                            // wipe the rows, so it doesn't need the discard
                            // confirm — but it does invalidate every row's hour
                            // total, so it dirties the plan and gets saved.
                            onChange={(e) => {
                                const parsed = Number(e.target.value);
                                setShiftHours(
                                    Number.isFinite(parsed) && parsed > 0
                                        ? normalizeHours(parsed)
                                        : 0,
                                );
                                setDirty(true);
                            }}
                            disabled={!canEdit}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 90 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size={'small'}
                                    checked={singleProductOnly}
                                    onChange={(e) =>
                                        setSingleProductOnly(e.target.checked)
                                    }
                                />
                            }
                            label={
                                <Typography variant={'caption'}>
                                    Tasas de un solo producto
                                </Typography>
                            }
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            variant={'outlined'}
                            startIcon={<ListAltIcon />}
                            onClick={() => setPedidosOpen(true)}
                        >
                            Pedidos por surtir
                        </Button>
                        {hasRelatedOrderProductions ? (
                            <Alert
                                severity={'warning'}
                                variant={'outlined'}
                                sx={{ flexBasis: '100%' }}
                            >
                                {'Ya existen producciones registradas para esta '}
                                {'combinaci\u00f3n de fecha, turno, sucursal y tipo de '}
                                {'producci\u00f3n. Revisa el dato real antes de guardar '}
                                {'o modificar la planeaci\u00f3n.'}
                            </Alert>
                        ) : null}
                    </Box>

                    {/* Two views of the same plan. The selectors above stay
                        outside them because they choose WHICH plan, which both
                        views are about.

                        Rendered as a conditional rather than through DTabs: its
                        `content` prop takes a component, and an inline one is a
                        new component type on every render — which remounts the
                        editor, and with it the focused input, on each keystroke. */}
                    <Tabs
                        value={tab}
                        onChange={(_, next: number) => setTab(next)}
                        sx={{ mb: 2, minHeight: 40 }}
                    >
                        <Tab label={'Planeación'} sx={{ minHeight: 40 }} />
                        <Tab label={'WhatsApp'} sx={{ minHeight: 40 }} />
                    </Tabs>

                    {optionsLoading || planLoading ? (
                        <DPageLoader />
                    ) : tab === WHATSAPP_TAB ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 3,
                                alignItems: 'flex-start',
                            }}
                        >
                            {/* One column per message. The notes that feed a
                                message sit directly above the preview of that
                                message, so a note typed here is visible in the
                                text below before anything is copied — which is
                                what the whole tab is for. */}
                            <WhatsAppMessage
                                title={'Parejas'}
                                notesTitle={'Notas de parejas'}
                                addLabel={'Agregar nota de parejas'}
                                notes={pairNotes}
                                onNotesChange={(items) => {
                                    setPairNotes(items);
                                    setDirty(true);
                                }}
                                preview={buildPairsText()}
                                copyLabel={'Copiar parejas'}
                                onCopy={handleCopyPairs}
                                copyDisabled={rows.length === 0}
                                canEdit={canEdit}
                            />
                            <WhatsAppMessage
                                title={'Productos'}
                                notesTitle={'Notas de productos'}
                                addLabel={'Agregar nota de productos'}
                                notes={productNotes}
                                onNotesChange={(items) => {
                                    setProductNotes(items);
                                    setDirty(true);
                                }}
                                preview={buildProductsText()}
                                copyLabel={'Copiar productos'}
                                onCopy={handleCopyProducts}
                                copyDisabled={!hasProductsContent}
                                canEdit={canEdit}
                            />
                        </Box>
                    ) : (
                        <>
                            {rows.length === 0 ? (
                                <Typography color={'text.secondary'}>
                                    No hay renglones en esta planeación
                                    {canEdit
                                        ? ' — agrega una máquina para empezar.'
                                        : '.'}
                                </Typography>
                            ) : (
                                rows.map((row, index) => (
                                    <ProductionPlanRow
                                        key={row.key}
                                        row={row}
                                        index={index}
                                        total={rows.length}
                                        machines={filteredMachines}
                                        allMachines={machines}
                                        products={products}
                                        employees={employees}
                                        inventory={inventory}
                                        canEdit={canEdit}
                                        shiftHours={shiftHours}
                                        employeeUsage={employeeUsage}
                                        getRate={getRate}
                                        ensureMachineProducts={ensureMachineProduct}
                                        getMachineProducts={getMachineProduct}
                                        actualByMachineProduct={
                                            actualByMachineProduct
                                        }
                                        plannedKilosByProduct={
                                            plannedKilosByProduct
                                        }
                                        plannedProductContributions={
                                            plannedProductContributions
                                        }
                                        plannedMachinesByProduct={
                                            plannedMachinesByProduct
                                        }
                                        expanded={expandedRowKey === row.key}
                                        onToggleExpand={handleToggleExpand}
                                        onChange={handleRowChange}
                                        onRemove={handleRemoveRow}
                                        onMove={handleMoveRow}
                                    />
                                ))
                            )}

                            {canEdit ? (
                                <Button
                                    variant={'outlined'}
                                    startIcon={<AddIcon />}
                                    onClick={handleAddRow}
                                    sx={{ mt: 1 }}
                                >
                                    Agregar máquina
                                </Button>
                            ) : null}
                        </>
                    )}
                </Box>
            </DPageBody>

            <PedidosDrawer
                open={pedidosOpen}
                onClose={() => setPedidosOpen(false)}
                pedidos={visiblePedidos}
                includePending={pedidosIncludePending}
                onIncludePendingChange={setPedidosIncludePending}
                productPlanStates={productPlanStates}
                plannedKilosByProduct={plannedKilosByProduct}
                productionNeededByProduct={productionNeededByProduct}
                isInventoryTracked={isInventoryTracked}
                ratesPending={ratesPending}
                canEdit={canEdit}
                onAdd={handleAddFromPedido}
            />

            <MachinePickerDialog
                productId={machinePickerProductId}
                productDescription={
                    machinePickerProductId
                        ? productDescription(machinePickerProductId)
                        : ''
                }
                groupWeight={
                    machinePickerProductId
                        ? productById.get(machinePickerProductId)
                              ?.current_group_weight
                        : null
                }
                candidates={machinePickerCandidates}
                onPick={handlePickMachine}
                onClose={() => setMachinePickerProductId(null)}
            />

            <DModal
                icon={<ImportExportIcon />}
                title={'Copiar de otro turno'}
                open={importOpen}
                setOpen={setImportOpen}
                dismissible
                size={'small'}
            >
                <Box sx={{ px: 2, py: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'flex-end',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ width: 180 }}>
                            <DDatePicker
                                views={['day']}
                                inputFormat={DATE_FORMAT}
                                label={'Fecha'}
                                value={importDate}
                                clearable={false}
                                disableGutters
                                onChange={(val) => {
                                    if (val) setImportDate(val);
                                }}
                            />
                        </Box>
                        <TextField
                            select
                            label={'Turno'}
                            size={'small'}
                            margin={'none'}
                            value={importShift}
                            onChange={(e) =>
                                setImportShift(Number(e.target.value))
                            }
                            sx={{ width: 100 }}
                        >
                            {SHIFTS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={'Sucursal'}
                            size={'small'}
                            margin={'none'}
                            value={importBranchId ?? ''}
                            SelectProps={{ displayEmpty: true }}
                            onChange={(e) =>
                                setImportBranchId(
                                    e.target.value === ''
                                        ? null
                                        : Number(e.target.value),
                                )
                            }
                            sx={{ width: 150 }}
                        >
                            <MenuItem value={''}>Todas</MenuItem>
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Typography
                        variant={'body2'}
                        color={'text.secondary'}
                        sx={{ mb: 2 }}
                    >
                        {importPreviewLine}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant={'contained'}
                            onClick={handleImport}
                            disabled={
                                importPreview.loading ||
                                importPreview.rows.length === 0
                            }
                        >
                            Importar
                        </Button>
                    </Box>
                </Box>
            </DModal>
        </DPage>
    );
}
